import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../config/api';
import { 
  Trophy, Crown, Medal, Award, TrendingUp, Star,
  ChevronUp, ChevronDown, Minus, Loader2, Users,
  Flame, Zap, Target
} from 'lucide-react';
import { cn } from '../lib/utils';
const API = `${API_BASE}/api`;

// Rank badge component with animations
function RankBadge({ rank }) {
  if (rank === 1) {
    return (
      <div className="relative">
        <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-500 rounded-full flex items-center justify-center shadow-lg shadow-amber-500/30 animate-pulse-slow">
          <Crown className="h-6 w-6 text-white" />
        </div>
        <div className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center text-[10px] font-bold text-black border-2 border-zinc-900">
          1
        </div>
      </div>
    );
  }
  
  if (rank === 2) {
    return (
      <div className="relative">
        <div className="w-11 h-11 bg-gradient-to-br from-zinc-300 via-zinc-400 to-zinc-500 rounded-full flex items-center justify-center shadow-lg shadow-zinc-400/20">
          <Medal className="h-5 w-5 text-white" />
        </div>
        <div className="absolute -top-1 -right-1 w-5 h-5 bg-zinc-300 rounded-full flex items-center justify-center text-[10px] font-bold text-zinc-800 border-2 border-zinc-900">
          2
        </div>
      </div>
    );
  }
  
  if (rank === 3) {
    return (
      <div className="relative">
        <div className="w-10 h-10 bg-gradient-to-br from-amber-600 via-amber-700 to-amber-800 rounded-full flex items-center justify-center shadow-lg shadow-amber-700/20">
          <Award className="h-5 w-5 text-white" />
        </div>
        <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-600 rounded-full flex items-center justify-center text-[10px] font-bold text-white border-2 border-zinc-900">
          3
        </div>
      </div>
    );
  }
  
  return (
    <div className="w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center text-sm font-bold text-zinc-400">
      {rank}
    </div>
  );
}

// Level badge with color coding
function LevelBadge({ level, levelName }) {
  const levelColors = {
    1: 'from-zinc-500 to-zinc-600',
    2: 'from-blue-500 to-blue-600',
    3: 'from-cyan-500 to-cyan-600',
    4: 'from-green-500 to-green-600',
    5: 'from-yellow-500 to-yellow-600',
    6: 'from-orange-500 to-orange-600',
    7: 'from-red-500 to-red-600',
    8: 'from-[#1EBFB3] to-[#1EBFB3]',
    9: 'from-pink-500 to-pink-600',
    10: 'from-amber-400 via-amber-500 to-orange-500',
  };
  
  return (
    <div className={cn(
      "px-2 py-0.5 rounded-full text-xs font-medium text-white bg-gradient-to-r",
      levelColors[level] || levelColors[1]
    )}>
      {levelName}
    </div>
  );
}

// Single leaderboard entry
function LeaderboardEntry({ entry, isCurrentUser, showAnimation }) {
  const [animate, setAnimate] = useState(showAnimation);
  
  useEffect(() => {
    if (showAnimation) {
      const timer = setTimeout(() => setAnimate(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [showAnimation]);
  
  return (
    <div className={cn(
      "flex items-center gap-4 p-4 rounded-xl transition-all duration-300",
      isCurrentUser 
        ? "bg-navigate-gold/10 border border-navigate-gold/30" 
        : "bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700",
      animate && "animate-slide-in-right"
    )}>
      {/* Rank */}
      <RankBadge rank={entry.rank} />
      
      {/* User Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={cn(
            "font-semibold truncate",
            isCurrentUser ? "text-navigate-gold" : "text-white"
          )}>
            {entry.user_name}
          </span>
          {isCurrentUser && (
            <span className="text-[10px] bg-navigate-gold/20 text-navigate-gold px-2 py-0.5 rounded-full">
              You
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1">
          <LevelBadge level={entry.level} levelName={entry.level_name} />
        </div>
      </div>
      
      {/* XP */}
      <div className="text-right">
        <div className="flex items-center gap-1 justify-end">
          <Zap className="h-4 w-4 text-navigate-gold" />
          <span className="text-lg font-bold text-navigate-gold">
            {entry.total_xp.toLocaleString()}
          </span>
        </div>
        <span className="text-xs text-zinc-500">XP</span>
      </div>
    </div>
  );
}

// Top 3 podium display
function TopThreePodium({ leaders }) {
  if (leaders.length < 3) return null;
  
  const [first, second, third] = leaders;
  
  return (
    <div className="flex items-end justify-center gap-4 mb-8 pt-8">
      {/* Second Place */}
      <div className="flex flex-col items-center">
        <div className="w-16 h-16 bg-gradient-to-br from-zinc-300 to-zinc-500 rounded-full flex items-center justify-center mb-2 shadow-lg">
          <span className="text-2xl font-bold text-white">2</span>
        </div>
        <div className="w-24 h-20 bg-gradient-to-t from-zinc-600 to-zinc-500 rounded-t-lg flex flex-col items-center justify-center">
          <span className="text-white font-semibold text-sm truncate w-20 text-center">
            {second?.user_name?.split(' ')[0] || 'N/A'}
          </span>
          <span className="text-zinc-300 text-xs">{second?.total_xp?.toLocaleString()} XP</span>
        </div>
      </div>
      
      {/* First Place */}
      <div className="flex flex-col items-center -mt-8">
        <div className="relative">
          <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-500 rounded-full flex items-center justify-center mb-2 shadow-xl shadow-amber-500/40 animate-pulse-slow">
            <Crown className="h-10 w-10 text-white" />
          </div>
          <div className="absolute -top-2 left-1/2 -translate-x-1/2">
            <Star className="h-6 w-6 text-yellow-400 fill-yellow-400 animate-bounce-slow" />
          </div>
        </div>
        <div className="w-28 h-28 bg-gradient-to-t from-amber-600 to-amber-500 rounded-t-lg flex flex-col items-center justify-center">
          <span className="text-white font-bold text-sm truncate w-24 text-center">
            {first?.user_name?.split(' ')[0] || 'N/A'}
          </span>
          <span className="text-amber-100 text-xs">{first?.total_xp?.toLocaleString()} XP</span>
          <LevelBadge level={first?.level} levelName={first?.level_name} />
        </div>
      </div>
      
      {/* Third Place */}
      <div className="flex flex-col items-center">
        <div className="w-14 h-14 bg-gradient-to-br from-amber-600 to-amber-800 rounded-full flex items-center justify-center mb-2 shadow-lg">
          <span className="text-xl font-bold text-white">3</span>
        </div>
        <div className="w-24 h-16 bg-gradient-to-t from-amber-800 to-amber-700 rounded-t-lg flex flex-col items-center justify-center">
          <span className="text-white font-semibold text-sm truncate w-20 text-center">
            {third?.user_name?.split(' ')[0] || 'N/A'}
          </span>
          <span className="text-amber-200 text-xs">{third?.total_xp?.toLocaleString()} XP</span>
        </div>
      </div>
    </div>
  );
}

// Main Leaderboard Component
export default function Leaderboard({ compact = false, limit = 10 }) {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [currentUserRank, setCurrentUserRank] = useState(null);
  const [currentUserXP, setCurrentUserXP] = useState(0);
  
  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const res = await axios.get(`${API}/gamification/leaderboard?limit=${limit}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLeaderboard(res.data.leaderboard || []);
        setCurrentUserRank(res.data.current_user_rank);
        setCurrentUserXP(res.data.current_user_xp);
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchLeaderboard();
    }
  }, [token, limit]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
      </div>
    );
  }
  
  if (leaderboard.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
        <h3 className="text-xl font-medium text-zinc-400 mb-2">No rankings yet</h3>
        <p className="text-zinc-600">Complete activities to appear on the leaderboard!</p>
      </div>
    );
  }
  
  // Compact version for dashboard sidebar
  if (compact) {
    return (
      <div className="space-y-2">
        {leaderboard.slice(0, 5).map((entry, idx) => (
          <div 
            key={entry.user_id}
            className={cn(
              "flex items-center gap-3 p-2 rounded-lg",
              entry.user_id === user?.user_id 
                ? "bg-navigate-gold/10" 
                : "bg-zinc-800/50"
            )}
          >
            <span className={cn(
              "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
              idx === 0 ? "bg-amber-500 text-black" :
              idx === 1 ? "bg-zinc-400 text-black" :
              idx === 2 ? "bg-amber-700 text-white" :
              "bg-zinc-700 text-zinc-400"
            )}>
              {entry.rank}
            </span>
            <span className="flex-1 truncate text-sm text-zinc-300">{entry.user_name}</span>
            <span className="text-xs text-navigate-gold font-medium">{entry.total_xp.toLocaleString()}</span>
          </div>
        ))}
        
        {/* Current user if not in top 5 */}
        {currentUserRank > 5 && (
          <>
            <div className="text-center text-zinc-600 text-xs py-1">• • •</div>
            <div className="flex items-center gap-3 p-2 rounded-lg bg-navigate-gold/10">
              <span className="w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center text-xs font-bold text-zinc-400">
                {currentUserRank}
              </span>
              <span className="flex-1 truncate text-sm text-navigate-gold">You</span>
              <span className="text-xs text-navigate-gold font-medium">{currentUserXP.toLocaleString()}</span>
            </div>
          </>
        )}
      </div>
    );
  }
  
  // Full leaderboard view
  return (
    <div className="space-y-6">
      {/* Top 3 Podium */}
      {leaderboard.length >= 3 && (
        <TopThreePodium leaders={leaderboard.slice(0, 3)} />
      )}
      
      {/* Rest of leaderboard */}
      <div className="space-y-2">
        {leaderboard.slice(3).map((entry) => (
          <LeaderboardEntry
            key={entry.user_id}
            entry={entry}
            isCurrentUser={entry.user_id === user?.user_id}
            showAnimation={false}
          />
        ))}
      </div>
      
      {/* Current user position if not in top 10 */}
      {currentUserRank > limit && (
        <div className="border-t border-zinc-800 pt-4">
          <p className="text-sm text-zinc-500 mb-2">Your Position</p>
          <LeaderboardEntry
            entry={{
              rank: currentUserRank,
              user_id: user?.user_id,
              user_name: user?.full_name || 'You',
              total_xp: currentUserXP,
              level: 1,
              level_name: 'Explorer',
              badges_count: 0
            }}
            isCurrentUser={true}
            showAnimation={true}
          />
        </div>
      )}
    </div>
  );
}

// Standalone page wrapper
export function LeaderboardPage() {
  return (
    <div className="min-h-screen bg-navigate-black">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-navigate-gold to-amber-600 rounded-2xl mb-4 shadow-lg shadow-navigate-gold/20">
            <Trophy className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Leaderboard</h1>
          <p className="text-zinc-400">Top learners in your organization</p>
        </div>
        
        {/* Leaderboard */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
          <Leaderboard limit={50} />
        </div>
      </div>
    </div>
  );
}
